import { useLDFlag } from '@/composables/use-ld-flag.ts';
import { usePublicApiFetch } from '@/services/api-client.ts';
import { useAuthStore } from '@/stores/auth-store.ts';
import { computed, reactive, ref } from 'vue';

/**
 * Bureau document statistics type
 */
export interface BureauStat {
   code: string;
   ingested: number;
   total: number;
   isLoading: boolean;
}

/**
 * Target numbers for total documents to be ingested per bureau
 * These are static values representing the eventual total documents
 */
export const BUREAU_TARGETS: Record<string, number> = {
   AQB: 473331,
   ASD: 0,
   CCB: 6555,
   CHB: 199,
   CPB: 0,
   DOEOB: 732,
   DWB: 0,
   EHB: 19354,
   GWQB: 278613,
   HWB: 100911,
   OGC: 0,
   OHSB: 320,
   ONRT: 843,
   OOTS: 0,
   OSI: 0,
   PSTB: 189847,
   RCB: 53227,
   RRB: 0,
   SWQB: 1473,
};

/**
 * All bureau codes in order
 */
export const BUREAU_CODES = Object.keys(BUREAU_TARGETS);

/**
 * Creates the initial bureau stats array
 */
function createBureauStats(): BureauStat[] {
   return BUREAU_CODES.map(code => ({
      code,
      ingested: 0,
      total: BUREAU_TARGETS[code],
      isLoading: true,
   }));
}

// Shared state across all usages (singleton pattern)
const bureauStats = reactive<BureauStat[]>(createBureauStats());
const hasFetchedData = ref(false);
const isFetching = ref(false);

/**
 * Composable for bureau document ingestion statistics.
 * Provides reactive bureau stats with lazy-loaded ingestion counts from the API.
 * Uses singleton pattern - data is shared and fetched only once across all components.
 */
export function useBureauStats() {
   const authStore = useAuthStore();
   const useAstraEndpoint = useLDFlag('astra_search_experiment', false);

   /**
    * Fetches the document count for a specific bureau from the API
    */
   async function fetchBureauCount(bureauCode: string): Promise<number> {
      try {
         const endpoint = useAstraEndpoint.value
            ? '/nm/nmed/agent/nmed_document_information_astra'
            : '/nm/nmed/agent/nmed_document_information';
         const response = await usePublicApiFetch<{ number_of_documents: number | string }>(
            endpoint,
            {
               method: 'POST',
               body: {
                  api_key: authStore.userApiKey?.api_key,
                  bureau: bureauCode,
                  first_row: '0',
                  number_of_rows: '1',
               },
            },
         );
         return Number(response.number_of_documents) || 0;
      }
      catch (e) {
         console.error(`Failed to fetch count for bureau ${bureauCode}:`, e);
         return 0;
      }
   }

   /**
    * Fetches document counts for all bureaus with total > 0
    * Only fetches once (lazy loading)
    */
   async function fetchAllBureauCounts() {
      if (hasFetchedData.value || isFetching.value)
         return;

      isFetching.value = true;

      // Only fetch for bureaus that have documents to ingest (total > 0)
      const bureausToFetch = bureauStats.filter(stat => stat.total > 0);

      const promises = bureausToFetch.map(async (bureau) => {
         const count = await fetchBureauCount(bureau.code);
         bureau.ingested = count;
         bureau.isLoading = false;
      });

      await Promise.all(promises);

      // Mark bureaus with total = 0 as not loading (they won't be fetched)
      bureauStats.filter(stat => stat.total === 0).forEach((bureau) => {
         bureau.isLoading = false;
      });

      hasFetchedData.value = true;
      isFetching.value = false;
   }

   /**
    * All bureau stats (includes bureaus with 0 total)
    */
   const allBureauStats = computed(() => bureauStats);

   /**
    * Only bureaus that have documents to be ingested (total > 0)
    */
   const bureausWithDocuments = computed(() => bureauStats.filter(stat => stat.total > 0));

   /**
    * Only bureaus that have documents already ingested (ingested > 0)
    */
   const bureausWithIngested = computed(() => bureauStats.filter(stat => stat.ingested > 0));

   /**
    * Total documents ingested across all bureaus
    */
   const totalIngested = computed(() => bureauStats.reduce((sum, b) => sum + b.ingested, 0));

   /**
    * Total documents to be ingested across all bureaus
    */
   const totalToBeIngested = computed(() => bureauStats.reduce((sum, b) => sum + b.total, 0));

   /**
    * Whether data has been fetched
    */
   const isDataLoaded = computed(() => hasFetchedData.value);

   /**
    * Whether data is currently being fetched
    */
   const isLoading = computed(() => isFetching.value);

   return {
      // Data
      allBureauStats,
      bureausWithDocuments,
      bureausWithIngested,

      // Totals
      totalIngested,
      totalToBeIngested,

      // State
      isDataLoaded,
      isLoading,

      // Actions
      fetchAllBureauCounts,
   };
}
